import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { InvoiceV2Service } from 'src/app/core/v2/_services/invoice-v2.service';
import { Invoice } from 'src/app/core/v2/types/invoice';
import { SubdomainService } from 'src/app/core/_base/subdomain.service';
import { LayoutUtilsService } from 'src/app/core/_base/crud';
import { environment } from 'src/environments/environment';
import { InvoiceDialogComponent, InvoiceDialogInput, InvoiceDialogReturn } from '../invoice-dialog/invoice-dialog.component';

@Component({
  selector: 'kt-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss']
})
export class InvoicesComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() toeID: number;
  @Input() currency: string;

  columns = ['invoice_number', 'amount', 'actions'];
  dataSource = new MatTableDataSource<Invoice>([]);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private cancel$: Subject<void> = new Subject();
  private reload$: Subject<void> = new Subject();

  constructor(
    private dialog: MatDialog,
    private invoiceService: InvoiceV2Service,
    private subDomainService: SubdomainService,
    private layoutUtilsService: LayoutUtilsService
  ) { }

  ngOnInit(): void {
    this.reload$.pipe(
      takeUntil(this.cancel$),
      switchMap(() => {
        this.loading$.next(true);
        return this.invoiceService.fetchByToe(this.toeID);
      })
    ).subscribe(invoices => {
      this.loading$.next(false);
      this.dataSource.data = invoices;
    });

    this.reload$.next();
  }

  ngOnDestroy() {
    this.cancel$.next();
    this.cancel$.complete();
  }

  public addInvoice() {
    this.openDialog('New Invoice', null).subscribe(res => {
      if (!res) {
        return;
      }
      this.loading$.next(true);
      this.invoiceService.create(this.toeID, res).pipe(takeUntil(this.cancel$)).subscribe(() => {
        this.reload$.next();
      });
    });
  }

  public editInvoice(invoice: Invoice) {
    this.openDialog('Invoice ' + invoice.invoice_number, invoice).subscribe(res => {
      if (!res) {
        return;
      }
      this.loading$.next(true);
      this.invoiceService.update(invoice.id, res).pipe(takeUntil(this.cancel$)).subscribe(() => {
        this.reload$.next();
      });
    });
  }

  public downloadFile(invoice: Invoice) {
    if (!invoice.file_path) {
      return;
    }
    const path = `${invoice.file_path}/${invoice.file_name}`;
    window.open(environment.baseApiUrl + `api/${this.subDomainService.subDomain}/files/download?path=` + path, '_blank');
  }

  public deleteInvoice(invoice: Invoice) {
    const _title = 'Warning';
    const _description = 'You are about to delete an invoice. This action is not reversible. Are you sure you want to proceed?';
    const _waitDesciption = '';

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        return;
      }
      this.loading$.next(true);
      this.invoiceService.delete(invoice.id).pipe(takeUntil(this.cancel$)).subscribe(() => {
        this.reload$.next();
      });
    });
  }

  private openDialog(title: string, invoice: Invoice | null) {
    const dialogRef = this.dialog.open<InvoiceDialogComponent, InvoiceDialogInput, InvoiceDialogReturn>(InvoiceDialogComponent, {
      data: {
        title,
        currency: this.currency,
        invoice
      },
      width: '640px'
    });
    return dialogRef.afterClosed();
  }
}
